import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '@/hooks/useRaiseBeacon';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Search, X, Building2, User, Briefcase, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Contact, Deal, Firm } from '@/types/backr';

export function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { results, isSearching, search } = useGlobalSearch();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) search(query);
    }, 150);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (result: { result_type: string; data: Contact | Deal | Firm }) => {
    setIsOpen(false);
    setQuery('');
    
    switch (result.result_type) {
      case 'contact':
        navigate(`/crm/contacts?contact=${(result.data as Contact).id}`);
        break;
      case 'deal':
        navigate(`/crm/deals?deal=${(result.data as Deal).id}`);
        break;
      case 'firm':
        navigate(`/crm/firms?firm=${(result.data as Firm).id}`);
        break;
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'contact': return <User className="h-4 w-4" />;
      case 'deal': return <Briefcase className="h-4 w-4" />;
      case 'firm': return <Building2 className="h-4 w-4" />;
      default: return <Search className="h-4 w-4" />;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(0);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSelectedIndex(i => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSelectedIndex(i => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && results[selectedIndex]) {
              handleSelect(results[selectedIndex]);
            }
          }}
          placeholder="Search investors, deals, firms... (⌘K)"
          className="pl-10 pr-10 h-10 bg-background/50 backdrop-blur-sm border-muted focus:bg-background transition-colors"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setIsOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {isOpen && (query || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background rounded-xl border shadow-2xl z-50 max-h-[60vh] overflow-auto">
          {isSearching ? (
            <div className="p-8 flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          ) : results.length === 0 ? (
            query.length >= 2 ? (
              <div className="p-8 text-center text-muted-foreground">
                No results found for "{query}"
              </div>
            ) : (
              <div className="p-4 space-y-2">
                <p className="text-xs text-muted-foreground px-2">Try searching for:</p>
                <div className="flex flex-wrap gap-2">
                  {['Sequoia', 'Series A', 'Fintech', 'Stanford', 'SaaS'].map(term => (
                    <button
                      key={term}
                      onClick={() => { setQuery(term); search(term); }}
                      className="px-3 py-1 text-sm bg-muted hover:bg-muted/80 rounded-full transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className="py-2">
              {results.map((result, index) => {
                const isSelected = index === selectedIndex;
                const data = result.data;
                
                return (
                  <button
                    key={`${result.result_type}-${data.id}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      "w-full px-4 py-3 flex items-center gap-3 text-left transition-colors",
                      isSelected ? "bg-[#2B7FFF]/10" : "hover:bg-muted/50"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-lg",
                      result.result_type === 'contact' && "bg-blue-100 text-blue-600",
                      result.result_type === 'deal' && "bg-green-100 text-green-600",
                      result.result_type === 'firm' && "bg-purple-100 text-purple-600"
                    )}>
                      {getIcon(result.result_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">
                          {'name' in data ? data.name : 'company_name' in data ? data.company_name : 'Unknown'}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {result.result_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {'title' in data && data.title && `${data.title} • `}
                        {'company' in data && data.company}
                        {'stage' in data && data.stage}
                        {'fund_size' in data && data.fund_size}
                      </p>
                    </div>
                    
                    {result.result_type === 'contact' && (
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs bg-[#2B7FFF]/10 text-[#2B7FFF]">
                          {(data as Contact).name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

